import { Box, Divider, List, Alert } from "@mui/material";
import React from "react";
import { useTranslation } from "react-i18next";
import AnalysisRow from "../common/components/AnalysisRow";
import { gql, useApolloClient, useMutation } from "@apollo/client";
import ConfirmDialog from "../common/dialogs/ConfirmDialog";

const REMOVE_ANALYSIS = gql(`
  mutation RemoveAnalysis($uuid: uuid!) {
    update_analyses(where: {uuid: {_eq: $uuid}}, _set: {deleted_at: "now()"}) {
      affected_rows
    }
  }
`);

export interface RecentMappingsProps {
  analysis: {
    uuid: string;
    created_at: string;
    state: string;
    agriculture_analysis_data: {
      orthophoto_png: string,
    },
    field: {
      name: string;
      uuid: string;
    }
  }[];
}

function RecentMappings(props: RecentMappingsProps) {
  const { t } = useTranslation();
  const apolloClient = useApolloClient();
  
  // State for confirm dialog
  const [confirmDialogOpen, setConfirmDialogOpen] = React.useState(false);
  const [analysisToDelete, setAnalysisToDelete] = React.useState<string | null>(null);
  
  // State for handling deletion status
  const [deleteStatus, setDeleteStatus] = React.useState<{ success: boolean; message: string } | null>(null);
  
  const [removeAnalysis] = useMutation(REMOVE_ANALYSIS, {
    onCompleted: () => {
      // Filter out the deleted analysis from the local state
      // This provides an immediate UI update without requiring a full refetch
      setDeleteStatus({ success: true, message: t('Inspection successfully deleted') });
      
      // Clear the success message after 3 seconds
      setTimeout(() => {
        setDeleteStatus(null);
      }, 3000);
    },
    onError: (error) => {
      console.error('Error deleting analysis:', error);
      setDeleteStatus({ success: false, message: t('Failed to delete inspection') });
      
      // Clear the error message after 3 seconds
      setTimeout(() => {
        setDeleteStatus(null);
      }, 3000);
    },
    // This ensures that any cached queries that depend on this data are updated
    refetchQueries: ['GetAnalyses']
  });

  return (
    <React.Fragment>
      {/* Status Notification */}
      {deleteStatus && (
        <div style={{ position: 'fixed', bottom: 20, left: '50%', transform: 'translateX(-50%)', zIndex: 1500 }}>
          <Alert 
            severity={deleteStatus.success ? 'success' : 'error'} 
            onClose={() => setDeleteStatus(null)}
          >
            {deleteStatus.message}
          </Alert>
        </div>
      )}
      
      <Box style={{
        margin: 20,
        marginBottom: 0,
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          fontSize: 20,
        }}>
          <span>{t('Recent Inspections')}</span>
        </div>
      </Box>
      <List sx={{ width: '100%' }}>
        {props.analysis.map((mapping, index) => <React.Fragment key={mapping.uuid}>
          <AnalysisRow analysis={mapping} prefix="/app/inspections/" onRemove={(uuid) => {
            setAnalysisToDelete(uuid);
            setConfirmDialogOpen(true);
          }} />
          { index < props.analysis.length - 1 && <Divider variant="inset" component="li" /> }
        </React.Fragment>)}
      </List>
      
      {/* Confirm Dialog */}
      <ConfirmDialog
        open={confirmDialogOpen}
        onClose={async (confirmed) => {
          setConfirmDialogOpen(false);
          
          if (confirmed && analysisToDelete) {
            await removeAnalysis({
              variables: {
                uuid: analysisToDelete
              }
            });

            await apolloClient.cache.reset();
            await apolloClient.resetStore();
          }
          
          setAnalysisToDelete(null);
        }}
        title={t('Delete Inspection')}
        body={t('Are you sure you want to delete this inspection? This action cannot be undone.')}
        confirmColor="error"
      />
    </React.Fragment>
  );
}

export default RecentMappings;
